import { useCallback, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Copy, LogOut, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { SettingsRow } from "~/pages/manifold-nextgen/components/settings/SettingsRow";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { useCanDeleteAgent } from "~/pages/manifold-nextgen/hooks/useCanDeleteAgent";
import { useCanDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useCanDuplicateAgent";
import { useCanLeaveAgent } from "~/pages/manifold-nextgen/hooks/useCanLeaveAgent";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { Agent, deleteAgent, leaveAgent, mutateAgentList } from "~/services/agents";
import { DeleteAgentDialog } from "./DeleteAgentDialog";
import { GiveUpOwnershipDialog } from "./GiveUpOwnershipDialog";

export const AgentSettings = () => {
  const agent = useOutletContext<Agent>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const canDuplicate = useCanDuplicateAgent(agent);
  const canDelete = useCanDeleteAgent(agent);
  const canLeave = useCanLeaveAgent(agent);
  const duplicateAgentAction = useDuplicateAgent();

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDuplicate = useCallback(
    () => duplicateAgentAction(agent.id, agent.display_name),
    [duplicateAgentAction, agent.id, agent.display_name]
  );

  // Throws on failure so GiveUpOwnershipDialog can surface the reason inline
  // (e.g. the "keep at least one owner" 409 when the caller is the only owner).
  const handleGiveUp = useCallback(async () => {
    await leaveAgent(agent.id);
    await mutateAgentList();
    navigate(`${ROUTE_NEXTGEN_VIEW}/agents`);
  }, [agent.id, navigate]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agent.id);
      await mutateAgentList();
      showSuccess(`"${agent.display_name}" deleted`);
      navigate(`${ROUTE_NEXTGEN_VIEW}/agents`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      {canDuplicate && (
        <SettingsSection title="General">
          <SettingsRow title="Duplicate agent" description="Create an editable copy of this agent.">
            <Button variant="outline" onClick={handleDuplicate}>
              <Copy className="size-4" />
              Duplicate
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}

      {(canLeave || canDelete) && (
        <SettingsSection title="Danger Zone" danger>
          {canLeave && (
            <SettingsRow
              title="Give up ownership"
              description="You'll lose access to this agent unless it's shared with you again."
            >
              <Button variant="outline" className="w-32" onClick={() => setLeaveOpen(true)}>
                <LogOut className="size-4" />
                Give up
              </Button>
            </SettingsRow>
          )}
          {canDelete && (
            <SettingsRow
              title="Delete agent"
              description="Permanently delete this agent. This action cannot be undone."
            >
              <Button variant="destructive" className="w-32" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="size-4" />
                Delete
              </Button>
            </SettingsRow>
          )}
        </SettingsSection>
      )}

      <GiveUpOwnershipDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        agentName={agent.display_name}
        onConfirm={handleGiveUp}
      />
      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
